/**
 * Dark-mode plumbing for the client-node shell (epic #1507 fidelity F2).
 *
 * Mirrors the original skin's own pre-paint boot script verbatim
 * (`.node-design/original-skin/core/boot/theme-init.js`): resolve `localStorage['nodeos-
 * theme']` first, then `matchMedia('(prefers-color-scheme: dark)')`, and — when dark —
 * add BOTH the `dark` and `menu-dark` classes (the source design gates surface/ink tokens
 * off `body.dark` and the menu's own cream-on-charcoal treatment off the separate
 * `body.menu-dark`, and always sets them together; see `core/shell/menu/menu.css` ~587-634).
 *
 * Two differences from the source script, both structural rather than behavioral:
 *   1. There is no `<body>` inside the shell's shadow root, so the classes go on TWO real
 *      elements instead of one: the `<node-app-shell>` HOST element (so the shadow-adopted
 *      skin sheet's own `:host(.dark)`/`:host(.menu-dark)` rules fire — see
 *      `skin/skin-stylesheet.ts` and the vendored `skin/css/*.css` rehomes) AND
 *      `document.documentElement` (so the document-level global sheet — tokens + fonts,
 *      `getSkinGlobalSheet()` — and native UA chrome like scrollbars follow the same
 *      theme for any light-DOM content, e.g. a `:root.dark` token consumer appended
 *      straight to `document.body`).
 *   2. The source script is a one-shot, anti-FOUC pre-paint IIFE with no live-update path
 *      (a dark-preferring OS that changes theme mid-session never un-darkens the page
 *      without a reload). This module additionally subscribes to the media query's
 *      `change` event so an unset preference stays live — but ONLY while no explicit
 *      `nodeos-theme` value is stored; an explicit choice (this shell's own future manual
 *      toggle, or a value carried over from the original skin's own toggle) always wins
 *      over a live OS-preference change, matching the source's own precedence order.
 *
 * Boundary note (#1681): this module never references `localStorage`/`matchMedia` itself —
 * both `initTheme` and `setThemePreference` take an injected `ThemeEnvironmentPort` (default
 * `currentThemeEnvironment()`, the composition-root-populated singleton in
 * `./theme-environment.js`), the same seam `client/adapters-web`'s `BrowserThemeEnvironment`
 * implements. An earlier version of this file instead defaulted a raw `win: Window = window`
 * parameter and called `win.localStorage`/`win.matchMedia` directly — real browser I/O
 * reached from `client/ui` through a parameter alias, invisible to
 * `client/scripts/check-boundaries.mjs` until it learned to resolve aliases. See
 * `ThemeEnvironmentPort`'s own doc comment (`client/ports/src/theme.ts`) for why this can't
 * instead be mediated through `@econ-v1/rpc`/the kernel the way ordinary UI I/O is.
 */
import { currentThemeEnvironment } from "./theme-environment.js";
const THEME_CLASSES = ["dark", "menu-dark"];
/**
 * Resolves the shell's initial theme and applies it to `hostEl` (the `<node-app-shell>`
 * shadow host) and `doc.documentElement`, then — when no preference is stored — keeps it
 * live against OS-preference changes for the lifetime of the returned handle.
 *
 * Callers MUST invoke this from `connectedCallback` BEFORE the shadow tree's first paint
 * (see `shell/app-shell.ts`) so the adopted skin stylesheet's `:host(.dark)`/
 * `:host(.menu-dark)` rules are already selecting correctly the moment content renders —
 * applying classes after paint would flash light-then-dark for a dark-preferring user.
 */
export function initTheme(hostEl, doc = document, env = currentThemeEnvironment()) {
    const stored = env.getStoredTheme();
    applyTheme(resolveDark(stored, env), hostEl, doc);
    if (stored !== undefined) {
        return { dispose() { } };
    }
    const unwatch = env.watchDarkColorScheme((matches) => {
        // Re-read the stored preference on every change rather than trusting the closed-over
        // `stored` snapshot: a manual toggle (`setThemePreference`) written after `initTheme`
        // ran must take precedence over a subsequent OS-level flip without requiring the
        // caller to also call `dispose()` — same "explicit choice beats live OS preference"
        // rule as the initial resolution above, just re-checked per event instead of once.
        if (env.getStoredTheme() !== undefined) {
            return;
        }
        applyTheme(matches, hostEl, doc);
    });
    return { dispose: unwatch ?? (() => { }) };
}
/**
 * Persists a manual theme choice under the same `nodeos-theme` key the original skin's
 * own toggle used, then applies it immediately. No manual-toggle UI calls this yet (epic
 * #1507 F2 scope is OS-preference fidelity only) — this is the storage-compatible plumbing
 * a future toggle wires a click handler to, kept here rather than duplicated per-caller.
 */
export function setThemePreference(preference, hostEl, doc = document, env = currentThemeEnvironment()) {
    env.setStoredTheme(preference);
    applyTheme(preference === "dark", hostEl, doc);
}
/**
 * Copies the shell's currently-resolved theme classes (`dark`/`menu-dark`, applied by
 * `initTheme`/`setThemePreference` onto `document.documentElement`) onto `target`.
 *
 * `initTheme` only ever touches TWO elements: the shell's own `<node-app-shell>` shadow
 * host and `document.documentElement` (see this module's own top-of-file doc comment,
 * point 1). Every OTHER shadow root that independently adopts the skin stylesheet — most
 * notably each per-stage shadow root `client/ui/src/runtime/stage-loader.ts`'s `loadRaw`
 * creates — starts with no theme class of its own. That silently pins such a root to the
 * skin's light-mode token defaults regardless of the shell's resolved theme: the adopted
 * sheet's own unconditional `:host, :root {...}` light block (`skin/skin-stylesheet.ts` /
 * `styles/tokens.css`) always matches that root's host (nothing about it depends on any
 * class), so it wins over the dark values inherited from `document.documentElement` — the
 * `:host(.dark)` override that would replace them never fires without the class physically
 * present on THAT SPECIFIC host element (custom-property inheritance only fills in for a
 * property with no closer declaration; it does not un-declare one). `document.documentElement`
 * stays the correct read source here (not the `<node-app-shell>` host) because it is the
 * one element `initTheme`/`setThemePreference` always keep current regardless of caller.
 */
export function syncThemeClasses(target, doc = document) {
    for (const className of THEME_CLASSES) {
        target.classList.toggle(className, doc.documentElement.classList.contains(className));
    }
}
function resolveDark(stored, env) {
    if (stored === "dark")
        return true;
    if (stored === "light")
        return false;
    return env.prefersDarkColorScheme();
}
function applyTheme(dark, hostEl, doc) {
    for (const className of THEME_CLASSES) {
        hostEl.classList.toggle(className, dark);
        doc.documentElement.classList.toggle(className, dark);
    }
}
//# sourceMappingURL=theme-init.js.map